import React, {
  Fragment,
  useCallback,
  useContext,
  useState,
  useEffect,
} from "react";
import makeStyles from "@mui/styles/makeStyles";
import { any, concat, identity, pipe, splitAt } from "ramda";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAngleRight,
  faAngleDown,
  faAngleUp,
  faPlus,
  faTrashCan,
} from "@fortawesome/pro-regular-svg-icons";
import { faSquareX } from "@fortawesome/pro-solid-svg-icons";
import { useCreatorModules } from "../../creatorServices";
import { useSearchQuery } from "../../common/use-searchQuery";
import { notBlank } from "../../../util/ext";
import { useCurrentOrgId, useIsAdmin } from "../../../auth/authUtils";
import {
  useCreateActiveModule,
  useDeleteActiveModule,
} from "../../../manage-organizations/organizationServices";
import { useModuleUpdate, useModuleDelete } from "../../modules/moduleServices";
import { isActive } from "../../modules/util";
import { deduplicate, useMediaPermissions } from "../../../util/mediaUtil";
import { GroupBrowserContext } from "../OrganizeView";

import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Button from "@mui/material/Button";
import Collapse from "@mui/material/Collapse";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Skeleton from "@mui/material/Skeleton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import AddNewModuleDialog from "../../modules/browse/AddNewModuleDialog";
import DeleteDialog from "../../common/DeleteDialog";
import ModuleCard from "./card/ModuleCard";
import PanelLoadingIndicator from "../../../components/PanelLoadingIndicator";
import StatusBadge from "../../common/StatusBadge";
import RealTimeSearchBar from "../../common/RealTimeSearchBar";
import { useAlert } from "../OrganizeProvider";
import PublishStatusDialog from "../../modules/edit/PublishStatusDialog";
import { useMediaQuery } from "@mui/material";

const useStyles = makeStyles(theme => ({
  accordionRoot: {
    cursor: "default !important",
    padding: 0,
    "&.Mui-focused,&.Mui-focusVisible": {
      backgroundColor: "white",
    },
  },

  accordionDetails: {
    flexDirection: "column",
    padding: 0,
  },

  accordionSummary: {
    minHeight: 90,
    margin: 0,
    "&.Mui-expanded": {
      margin: 0,
    },
  },

  divider: {
    height: 1,
    color: theme.palette.secondary.light,
    backgroundColor: "rgba(0, 0, 0, 0.12)",
    border: "none",
  },

  expandIcon: {
    marginRight: theme.spacing(3),
    width: 15,
    height: 15,
  },

  groups: {
    gap: 10,
    padding: 0,
    maxHeight: 250,
    overflow: "auto",
  },

  mediumStartIcon: {
    "& > :first-child": {
      fontSize: "1.4rem",
    },
  },
  selectBtn: {
    fontWeight: 700,
    marginLeft: 15,
  },

  startIcon: {
    "& > :first-child": {
      fontSize: "1.6rem",
    },
  },
}));

const initialModulesState = {
  appliedQuery: "",
  page: 0,
  limit: 10,
  orderBy: "auditUpdated",
  orderDirection: "desc",
  status: "ACTIVE,INACTIVE",
};

const statusBadgeLabel = {
  ACTIVE: "Push Live",
  INACTIVE: "Make Draft",
};

const getOppositeStatus = selected =>
  selected.status === "INACTIVE" ? "ACTIVE" : "INACTIVE";

const ModulesPanel = () => {
  const classes = useStyles();
  const isAdmin = useIsAdmin();
  const currentOrgId = useCurrentOrgId();
  const groupBrowser = useContext(GroupBrowserContext);
  const alert = useAlert();
  const isSm = useMediaQuery(theme => theme.breakpoints.up("sm"));
  const isXl = useMediaQuery(theme => theme.breakpoints.up("md"));
  const isMd = useMediaQuery(theme => theme.breakpoints.up("mid"));
  const visibleCount = isXl ? 5 : isMd ? 4 : 3;

  //-- Manage Modules Search State
  const modulesQuery = useSearchQuery(initialModulesState);
  const [modulesQueryState, setModulesQueryState] = useState({
    limit: initialModulesState.limit,
    offset: modulesQuery.state.limit * modulesQuery.state.page,
    sort: `${modulesQuery.state.orderBy} ${modulesQuery.state.orderDirection}`,
    status: initialModulesState.status,
    q: modulesQuery.state.appliedQuery,
  });

  useEffect(() => {
    const { limit, page, appliedQuery } = modulesQuery.state;
    const sort =
      modulesQuery.state.orderBy === null
        ? `${initialModulesState.orderBy} ${initialModulesState.orderDirection}`
        : `${modulesQuery.state.orderBy} ${modulesQuery.state.orderDirection}`;

    setModulesQueryState({
      limit,
      offset: limit * page,
      sort,
      status: initialModulesState.status,
      q: appliedQuery,
    });
  }, [modulesQuery.state]);

  const creatorModules = useCreatorModules(modulesQueryState);
  const [modules, setModules] = useState({
    data: [],
    hasMore: null,
    count: null,
  });

  //-- Manage Cards
  useEffect(() => {
    if (creatorModules.isSuccess) {
      setModules(prev => ({
        data: pipe(
          concat(
            modulesQuery.state.page === 0
              ? []
              : concat(prev.data.shown, prev.data.hidden),
          ),
          deduplicate,
          splitAt(visibleCount),
          ([shown, hidden]) => ({ shown, hidden }),
        )(creatorModules.data.contents),
        hasMore: creatorModules.data.hasMore,
        count: creatorModules.data.count,
      }));
    }
  }, [
    modulesQuery.state,
    creatorModules.isSuccess,
    creatorModules.data,
    visibleCount,
  ]);

  const handleNextPage = () =>
    modulesQuery.handleUpdatePage(modulesQuery.state.page + 1);

  //-- Manage Accordion State
  const handleOuterChange = () =>
    groupBrowser.setExpanded(prev => ({
      ...prev,
      modules: {
        inner: false,
        outer: !prev.modules.outer,
      },
    }));
  const handleInnerChange = () =>
    groupBrowser.setExpanded(prev => ({
      ...prev,
      modules: {
        ...prev.modules,
        inner: !prev.modules.inner,
      },
    }));

  // Manage New Module Dialog
  const [open, setOpen] = useState(false);
  const handleCreateNewClick = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const handleSave = () => handleClose();

  const [selected, setSelected] = useState({});
  const clearSelected = () => setSelected({});
  const updateModule = useModuleUpdate();
  const createActiveModule = useCreateActiveModule();
  const deleteActiveModule = useDeleteActiveModule();
  const [statusDialog, setStatusDialog] = useState(false);
  const handleStatusChange = useCallback(async () => {
    groupBrowser.setLoading(true);
    try {
      setStatusDialog(false);
      const newStatus = getOppositeStatus(selected);
      const variables = {
        resourceIds: {
          moduleId: selected.id,
          orgId: currentOrgId,
        },
      };
      if (isActive(selected)) {
        await deleteActiveModule.mutateAsync(variables);
      } else {
        await createActiveModule.mutateAsync(variables);
      }

      await updateModule.mutateAsync({
        resourceIds: { id: selected.id },
        data: {
          status: newStatus,
        },
      });

      setSelected({});
    } catch (err) {
      console.log(err);
    }
    groupBrowser.setLoading(false);
  }, [
    currentOrgId,
    createActiveModule,
    deleteActiveModule,
    groupBrowser,
    selected,
    updateModule,
  ]);

  // Reactivate Module
  const handleUndo = previousModule => async () => {
    groupBrowser.setLoading(true);
    try {
      await createActiveModule.mutateAsync({
        resourceIds: {
          moduleId: previousModule.id,
          orgId: previousModule.orgId,
        },
      });

      await updateModule.mutateAsync({
        resourceIds: { id: previousModule.id },
        data: { status: previousModule.status },
      });
    } catch (err) {
      alert.show("error", [err.message]);
    }
    groupBrowser.setLoading(false);
  };

  const { deletable } = useMediaPermissions(selected);
  const deleteModule = useModuleDelete();
  const handleDelete = async () => {
    await deleteModule.mutateAsync({ module: selected, orgId: currentOrgId });

    alert.update({
      actionButtonLabel: "undo",
      showAction: true,
      onAlertClick: handleUndo(selected),
    });
  };

  return (
    <Fragment>
      <AddNewModuleDialog
        open={open}
        onClose={handleClose}
        onSave={handleSave}
      />
      <PublishStatusDialog
        open={statusDialog}
        action={selected.status === "ACTIVE" ? "unpublish" : "publish"}
        handleStatusChange={handleStatusChange}
        handleClickClose={() => setStatusDialog(false)}
      />
      <Accordion expanded={groupBrowser.expanded.modules.outer}>
        <AccordionSummary
          aria-label={`Expand to see more modules`}
          classes={{
            root: classes.accordionRoot,
            content: classes.accordionSummary,
          }}
        >
          <Grid container justifyContent="space-between" alignItems="center">
            <Grid item container xs={7} md={6} alignItems="center">
              <Grid item>
                <IconButton
                  className={classes.expandIcon}
                  onClick={handleOuterChange}
                  size="large"
                >
                  <FontAwesomeIcon
                    icon={
                      groupBrowser.expanded.modules.outer
                        ? faAngleDown
                        : faAngleRight
                    }
                  />
                </IconButton>
              </Grid>

              <Grid item>
                <Typography variant="h2">
                  {creatorModules.isLoading ? (
                    <Skeleton width={125} />
                  ) : (
                    <Fragment>
                      Modules{" "}
                      {modules.count === null ? "-" : `(${modules.count})`}
                    </Fragment>
                  )}
                </Typography>
              </Grid>

              {notBlank(selected) && !isAdmin && (
                <Fragment>
                  <Grid item>
                    <Button
                      onClick={() => setSelected({})}
                      startIcon={<FontAwesomeIcon icon={faSquareX} />}
                      classes={{
                        root: classes.selectBtn,
                        iconSizeMedium: classes.mediumStartIcon,
                      }}
                    >
                      1 Selected
                    </Button>
                  </Grid>

                  <Grid item>
                    <Button onClick={() => setStatusDialog(true)}>
                      <StatusBadge
                        size="sm"
                        status={getOppositeStatus(selected)}
                        label={
                          <p style={{ margin: 0 }}>
                            {statusBadgeLabel[getOppositeStatus(selected)]}
                          </p>
                        }
                      />
                    </Button>
                  </Grid>

                  {deletable && (
                    <Grid item>
                      <DeleteDialog
                        handleDelete={handleDelete}
                        renderButton={onDelete => (
                          <Tooltip title="Delete">
                            <IconButton
                              onClick={() => onDelete(clearSelected)}
                              size="large"
                            >
                              <FontAwesomeIcon icon={faTrashCan} />
                            </IconButton>
                          </Tooltip>
                        )}
                      />
                    </Grid>
                  )}
                </Fragment>
              )}
            </Grid>

            <Grid
              container
              item
              xs={5}
              md={6}
              justifyContent="flex-end"
              alignItems="center"
            >
              <Grid item xs>
                <RealTimeSearchBar
                  searchBarEnd
                  query={modulesQuery.state.appliedQuery}
                  onQueryChange={modulesQuery.handleUpdateQuery}
                />
              </Grid>
              <Grid item xs="auto">
                <Tooltip title="Create Module">
                  <Button
                    variant="contained"
                    color="primary"
                    style={{ width: "auto" }}
                    classes={{
                      startIcon: classes.startIcon,
                    }}
                    startIcon={<FontAwesomeIcon icon={faPlus} />}
                    onClick={handleCreateNewClick}
                  >
                    Create
                  </Button>
                </Tooltip>
              </Grid>
            </Grid>
          </Grid>
        </AccordionSummary>
        <AccordionDetails classes={{ root: classes.accordionDetails }}>
          <Grid className={classes.groups} container>
            {any(identity, [
              creatorModules.isLoading,
              groupBrowser.loading,
            ]) && <PanelLoadingIndicator transparent />}

            <Grid
              container
              item
              justifyContent="flex-start"
              style={{ gap: isSm ? 5 : 10 }}
            >
              {notBlank(modules.data.shown) &&
                modules.data.shown.map(module => (
                  <ModuleCard
                    key={module.id}
                    module={module}
                    selected={selected}
                    setSelected={setSelected}
                  />
                ))}
            </Grid>

            <Collapse
              in={groupBrowser.expanded.modules.inner}
              style={{ width: "100%" }}
            >
              <Grid
                container
                item
                justifyContent="flex-start"
                style={{ gap: isSm ? 5 : 10 }}
              >
                {notBlank(modules.data.hidden) &&
                  modules.data.hidden.map(module => (
                    <ModuleCard
                      key={module.id}
                      module={module}
                      selected={selected}
                      setSelected={setSelected}
                    />
                  ))}
              </Grid>

              {modules.hasMore && (
                <Grid
                  container
                  item
                  xs
                  alignItems="center"
                  justifyContent="center"
                >
                  <Button color="primary" onClick={handleNextPage}>
                    More
                  </Button>
                </Grid>
              )}
            </Collapse>
          </Grid>

          {notBlank(modules.data.hidden) ? (
            <Grid container alignItems="center">
              <Grid item xs>
                <hr className={classes.divider} />
              </Grid>

              <Grid item>
                <Button
                  color="primary"
                  startIcon={
                    <FontAwesomeIcon
                      icon={
                        groupBrowser.expanded.modules.inner
                          ? faAngleUp
                          : faAngleDown
                      }
                    />
                  }
                  onClick={handleInnerChange}
                >
                  {groupBrowser.expanded.modules.inner ? "Collapse" : "Expand"}
                </Button>
              </Grid>

              <Grid item xs>
                <hr className={classes.divider} />
              </Grid>
            </Grid>
          ) : (
            <hr className={classes.divider} style={{ margin: "20px 0" }} />
          )}
        </AccordionDetails>
      </Accordion>
      {!groupBrowser.expanded.modules.outer && (
        <hr className={classes.divider} />
      )}
    </Fragment>
  );
};

export default ModulesPanel;
